import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDateLong } from "@/utils/formatDate";
import type { GalleryVideo } from "./GalleryVideoCard";
import { getVideoDownloadUrl } from "@/api/galleriesApi";

interface GalleryVideoModalProps {
  video: GalleryVideo | null;
  open: boolean;
  onClose: () => void;
  allowDownload: boolean;
  gallerySlug: string;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatVideoDate(isoString: string): string {
  return formatDateLong(isoString);
}

export default function GalleryVideoModal({
  video,
  open,
  onClose,
  allowDownload,
  gallerySlug,
}: GalleryVideoModalProps) {
  const { t } = useTranslation("gallery");

  if (!video) return null;

  const handleDownload = () => {
    window.open(getVideoDownloadUrl(gallerySlug, video.id), "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: "#111",
          color: "#fff",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {video.duration != null && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              {formatDuration(video.duration)}
            </Typography>
          )}
          {video.created_at && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              {formatVideoDate(video.created_at)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {allowDownload && (
            <IconButton
              onClick={handleDownload}
              sx={{ color: "rgba(255,255,255,0.8)" }}
              aria-label={t("modal.download")}
            >
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{ color: "rgba(255,255,255,0.8)" }}
            aria-label={t("modal.close")}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Video content */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
          minHeight: 400,
        }}
      >
        {video.embed_url ? (
          <Box
            component="iframe"
            src={`${video.embed_url}?autoplay=true&preload=true`}
            allow="autoplay; encrypted-media; fullscreen"
            title={`Video ${video.id}`}
            sx={{
              width: "100%",
              aspectRatio: "16/9",
              maxHeight: "70vh",
              border: "none",
              borderRadius: 1,
            }}
          />
        ) : (
          <Box
            component="img"
            loading="lazy"
            src={video.thumbnail_url || undefined}
            alt=""
            sx={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        )}
      </Box>
    </Dialog>
  );
}
